import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  History,
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Activity as ActivityIcon,
  Loader2,
  Search,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { crmListMyActivity } from "@/lib/crm.functions";

export const Route = createFileRoute("/crm/logs")({
  component: LogsPage,
});

const CHANNEL_META: Record<
  string,
  { icon: any; tint: string; ring: string; label: string }
> = {
  email: {
    icon: Mail,
    tint: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    ring: "ring-blue-500/30",
    label: "Email",
  },
  call: {
    icon: Phone,
    tint: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    ring: "ring-emerald-500/30",
    label: "Call",
  },
  whatsapp: {
    icon: MessageCircle,
    tint: "bg-green-500/10 text-green-600 dark:text-green-400",
    ring: "ring-green-500/30",
    label: "WhatsApp",
  },
  visit: {
    icon: MapPin,
    tint: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    ring: "ring-purple-500/30",
    label: "Visit",
  },
  other: {
    icon: ActivityIcon,
    tint: "bg-muted text-muted-foreground",
    ring: "ring-border",
    label: "Other",
  },
};

const CHANNEL_KEYS = ["email", "call", "whatsapp", "visit", "other"] as const;

function LogsPage() {
  const fetchLogs = useServerFn(crmListMyActivity);
  const q = useQuery({
    queryKey: ["crm", "logs", "all"],
    queryFn: () => fetchLogs(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const logs = q.data?.logs ?? [];

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    logs.forEach((l: any) => {
      c[l.channel] = (c[l.channel] || 0) + 1;
    });
    return c;
  }, [logs]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return logs.filter((l: any) => {
      if (filter !== "all" && l.channel !== filter) return false;
      if (!s) return true;
      return (
        l.summary?.toLowerCase().includes(s) ||
        l.client_name?.toLowerCase().includes(s) ||
        l.actor_name?.toLowerCase().includes(s)
      );
    });
  }, [logs, filter, search]);

  // Group by category (channel)
  const grouped = useMemo(() => {
    const map = new Map<string, any[]>();
    filtered.forEach((l: any) => {
      const k = l.channel || "other";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(l);
    });
    return Array.from(map.entries()).sort(
      (a, b) => (counts[b[0]] || 0) - (counts[a[0]] || 0),
    );
  }, [filtered, counts]);

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground">
          {logs.length}
        </span>
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity, client, or person…"
            className="h-9 pl-8 text-sm"
          />
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setFilter("all")}
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
            filter === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          All <span className="opacity-70">{logs.length}</span>
        </button>
        {CHANNEL_KEYS.map((key) => {
          if (!counts[key]) return null;
          const meta = CHANNEL_META[key];
          const Icon = meta.icon;
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                active
                  ? "bg-primary text-primary-foreground"
                  : `${meta.tint} hover:opacity-80`
              }`}
            >
              <Icon className="h-3 w-3" aria-hidden="true" />
              {meta.label} <span className="opacity-70">{counts[key]}</span>
            </button>
          );
        })}
      </div>

      {q.isLoading ? (
        <div className="grid place-items-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        </div>
      ) : q.isError || q.data?.error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {q.data?.error || "Couldn't load activity logs. Please try again."}
        </div>
      ) : grouped.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          <History className="mx-auto mb-2 h-6 w-6 opacity-60" aria-hidden="true" />
          No activity found.
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([channel, items]) => {
            const meta = CHANNEL_META[channel] ?? CHANNEL_META.other;
            const Icon = meta.icon;
            return (
              <section key={channel}>
                <header className="mb-2 flex items-center gap-2">
                  <span
                    className={`grid h-6 w-6 place-items-center rounded-lg ${meta.tint}`}
                    aria-hidden="true"
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <h2 className="text-sm font-semibold">{meta.label}</h2>
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-muted-foreground">
                    {items.length}
                  </span>
                </header>
                <ol className="relative space-y-2 border-l border-border pl-4">
                  {items.map((l: any) => (
                    <li
                      key={l.id}
                      className="relative rounded-md border border-border bg-card p-3 text-sm"
                    >
                      <span
                        className={`absolute -left-[26px] top-3 grid h-5 w-5 place-items-center rounded-full ring-2 ring-background ${meta.tint}`}
                        aria-hidden="true"
                      >
                        <Icon className="h-3 w-3" />
                      </span>
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                        <div className="inline-flex min-w-0 items-center gap-1.5">
                          {l.client_id ? (
                            <Link
                              to="/crm/$clientId"
                              params={{ clientId: l.client_id }}
                              className="inline-flex items-center gap-1 truncate font-semibold text-foreground hover:text-primary"
                            >
                              <span className="truncate">
                                {l.client_name || "Unknown client"}
                              </span>
                              <ExternalLink className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
                            </Link>
                          ) : (
                            <span className="truncate font-semibold text-foreground">
                              {l.client_name || "—"}
                            </span>
                          )}
                          {l.actor_name && (
                            <>
                              <span className="text-border" aria-hidden="true">·</span>
                              <span className="truncate">{l.actor_name}</span>
                            </>
                          )}
                        </div>
                        <span className="shrink-0 tabular-nums">
                          {new Date(l.occurred_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1.5 whitespace-pre-wrap break-words text-foreground">
                        {l.summary}
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
